import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Home, Bell, Settings, Search } from 'lucide-react';

/* Shared app-window frame: icon sidebar + top bar, so every illustration
   reads as a real mini application rather than a floating card */
const MiniAppFrame = ({ title, activeIcon: ActiveIcon, children }) => {
  const sidebarIcons = [Home, Search, Bell, Settings];
  return (
    <div className="w-full min-h-[420px] md:min-h-[460px] rounded-2xl bg-white border border-slate-100 shadow-xl overflow-hidden flex">
      {/* icon rail */}
      <div className="w-14 bg-[#003333] flex flex-col items-center py-5 gap-4 shrink-0">
        <div className="w-8 h-8 rounded-lg bg-[#66B3B3] flex items-center justify-center mb-2">
          <ActiveIcon className="w-4 h-4 text-[#003333]" />
        </div>
        {sidebarIcons.map((Icon, i) => (
          <Icon key={i} className={`w-4 h-4 ${i === 0 ? 'text-white' : 'text-white/30'}`} />
        ))}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50">
          <span className="w-2 h-2 rounded-full bg-slate-300" />
          <span className="w-2 h-2 rounded-full bg-slate-300" />
          <span className="w-2 h-2 rounded-full bg-slate-300" />
          <span className="ml-2 text-xs font-medium text-slate-400">{title}</span>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, delay = 0 }) => (
  <motion.div
    className="rounded-lg bg-slate-50 px-3 py-2.5 flex-1"
    animate={{ backgroundColor: ['#f8fafc', '#f0f9f8', '#f8fafc'] }}
    transition={{ duration: 5, repeat: Infinity, delay, ease: 'easeInOut' }}
  >
    <p className="text-[9px] text-slate-400 mb-1">{label}</p>
    <p className="text-sm font-bold text-[#003333]">{value}</p>
  </motion.div>
);

/* ---------- Project & Team Systems ---------- */
export const ProjectIllustration = () => {
  const columns = [
    { label: 'To do', count: 4 },
    { label: 'In progress', count: 2 },
    { label: 'Done', count: 6 },
  ];
  const [activeCol, setActiveCol] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActiveCol((c) => (c + 1) % 3);
    }, 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <MiniAppFrame title="Sprint board" activeIcon={Home}>
      <div className="flex items-center gap-2 mb-4">
        {['#66B3B3', '#003333', '#94a3b8'].map((c, i) => (
          <motion.span
            key={i}
            className="w-6 h-6 rounded-full border-2 border-white shadow -ml-2 first:ml-0"
            style={{ backgroundColor: c }}
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 3, repeat: Infinity, delay: i * 0.4 }}
          />
        ))}
        <span className="text-[10px] text-slate-400 ml-1">3 online now</span>
      </div>

      <div className="grid grid-cols-3 gap-3 h-40">
        {columns.map((col, i) => (
          <div key={col.label} className="rounded-lg bg-slate-50 p-2 flex flex-col gap-1.5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[9px] font-semibold text-slate-500">{col.label}</p>
              <span className="text-[8px] text-slate-400">{col.count}</span>
            </div>
            {activeCol === i ? (
              <motion.div
                layoutId="task-chip"
                transition={{ type: 'spring', stiffness: 300, damping: 28 }}
                className="h-7 rounded-md bg-[#66B3B3] shadow-md flex items-center px-2"
              >
                <span className="text-[8px] font-medium text-white truncate">Homepage redesign</span>
              </motion.div>
            ) : (
              <div className="h-7" />
            )}
            <div className="h-6 rounded-md bg-white border border-slate-100" />
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <motion.div
            className="h-full bg-[#66B3B3]"
            animate={{ width: ['30%', '68%', '30%'] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>
        <span className="text-[9px] text-slate-400">Sprint progress</span>
      </div>
    </MiniAppFrame>
  );
};

/* ---------- Sales & CRM Dashboards ---------- */
export const SalesIllustration = () => {
  const stages = ['Lead', 'Qualified', 'Proposal', 'Won'];
  const [activeStage, setActiveStage] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActiveStage((s) => (s + 1) % stages.length);
    }, 1800);
    return () => clearInterval(id);
  }, [stages.length]);

  const clients = [
    { initials: 'AK', name: 'Aria K.', color: '#66B3B3' },
    { initials: 'DM', name: 'Denis M.', color: '#003333' },
  ];
  return (
    <MiniAppFrame title="Sales pipeline" activeIcon={Home}>
      <div className="flex gap-3 mb-4">
        <StatCard label="Pipeline value" value="£184,200" />
        <StatCard label="Win rate" value="42%" delay={0.6} />
        <StatCard label="Open deals" value="27" delay={1.2} />
      </div>

      <div className="relative flex justify-between mb-6 px-1">
        <div className="absolute top-[6px] left-[4%] right-[4%] h-px bg-slate-200" />
        {stages.map((stage, i) => (
          <div key={stage} className="flex flex-col items-center gap-2 z-10">
            {activeStage === i ? (
              <motion.div
                layoutId="pipeline-dot"
                transition={{ type: 'spring', stiffness: 300, damping: 26 }}
                className="w-3.5 h-3.5 rounded-full bg-[#66B3B3] shadow"
              />
            ) : (
              <div className="w-3 h-3 rounded-full bg-slate-200" />
            )}
            <span className="text-[9px] text-slate-400">{stage}</span>
          </div>
        ))}
      </div>

      <div className="mb-3">
        {clients.map((c, i) => (
          <motion.div
            key={c.name}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 mb-1.5 last:mb-0"
            animate={{ backgroundColor: ['#ffffff', '#f8fafc', '#ffffff'] }}
            transition={{ duration: 4, repeat: Infinity, delay: i * 1.2, ease: 'easeInOut' }}
          >
            <span
              className="w-6 h-6 rounded-full text-white text-[9px] font-semibold flex items-center justify-center shrink-0"
              style={{ backgroundColor: c.color }}
            >
              {c.initials}
            </span>
            <span className="text-[10px] text-slate-600 flex-1">{c.name}</span>
            <span className="text-[9px] font-medium text-[#004D4D] bg-[#66B3B3]/15 rounded-full px-2 py-0.5">
              Active deal
            </span>
          </motion.div>
        ))}
      </div>

      <div className="flex items-end gap-1 h-12">
        {[35, 55, 40, 70, 50, 85].map((h, i) => (
          <motion.div
            key={i}
            className="flex-1 rounded-t-sm bg-[#003333]/80"
            animate={{ height: [`${h * 0.6}%`, `${h}%`, `${h * 0.7}%`] }}
            transition={{ duration: 3 + i * 0.3, repeat: Infinity, ease: 'easeInOut', delay: i * 0.15 }}
          />
        ))}
      </div>
    </MiniAppFrame>
  );
};

/* ---------- Finance & Reporting ---------- */
export const FinanceIllustration = () => {
  const rows = [
    { label: 'Invoice #2291', amount: '£1,240' },
    { label: 'Invoice #2292', amount: '£860' },
    { label: 'Invoice #2293', amount: '£2,015' },
  ];
  return (
    <MiniAppFrame title="Finance overview" activeIcon={Home}>
      <div className="flex gap-3 mb-4">
        <StatCard label="Revenue" value="£28,410" />
        <StatCard label="Expenses" value="£9,120" delay={0.6} />
        <StatCard label="Profit" value="£19,290" delay={1.2} />
      </div>

      <p className="text-[10px] font-semibold text-slate-500 mb-2">Recent invoices</p>
      {rows.map((row, i) => (
        <motion.div
          key={row.label}
          className="flex items-center justify-between rounded-lg px-2.5 py-2 mb-1.5"
          animate={{ backgroundColor: ['#ffffff', '#f0f9f8', '#ffffff'] }}
          transition={{ duration: 4.5, repeat: Infinity, delay: i * 1.4, ease: 'easeInOut' }}
        >
          <span className="text-[10px] text-slate-600">{row.label}</span>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium text-slate-500">{row.amount}</span>
            <motion.svg width="10" height="10" viewBox="0 0 24 24" fill="none">
              <motion.path
                d="M5 13l4 4L19 7"
                stroke="#66B3B3"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: [0, 1, 1] }}
                transition={{ duration: 4.5, repeat: Infinity, delay: i * 1.4, times: [0, 0.3, 1] }}
              />
            </motion.svg>
          </div>
        </motion.div>
      ))}

      <div className="flex items-end gap-1 h-14 mt-3">
        {[30, 60, 45, 80, 55, 90, 70].map((h, i) => (
          <motion.div
            key={i}
            className="flex-1 rounded-t-sm bg-[#66B3B3]/70"
            animate={{ height: [`${h * 0.5}%`, `${h}%`, `${h * 0.6}%`] }}
            transition={{ duration: 3.4 + i * 0.2, repeat: Infinity, ease: 'easeInOut', delay: i * 0.12 }}
          />
        ))}
      </div>
    </MiniAppFrame>
  );
};

/* ---------- Secure Internal Tools ---------- */
export const SecureIllustration = () => {
  const roles = ['Finance team', 'Support team', 'Admin (you)'];
  const logs = ['A. Karim signed in · 2m ago', 'Export approved · 14m ago', 'New role granted · 1h ago'];
  return (
    <MiniAppFrame title="Access control" activeIcon={Home}>
      <div className="flex items-center gap-4 mb-4">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="shrink-0">
          <rect x="5" y="11" width="14" height="9" rx="2" fill="#66B3B3" opacity="0.15" />
          <rect x="5" y="11" width="14" height="9" rx="2" stroke="#66B3B3" strokeWidth="1.5" />
          <motion.path
            d="M8 11V8a4 4 0 0 1 8 0v3"
            stroke="#003333"
            strokeWidth="1.8"
            fill="none"
            strokeLinecap="round"
            animate={{ rotate: [0, -10, 0], y: [0, -1.5, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            style={{ transformOrigin: '12px 11px' }}
          />
          <motion.circle
            cx="12" cy="15" r="1.6" fill="#003333"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
        </svg>
        <div>
          <p className="text-xs font-semibold text-[#003333]">All systems secure</p>
          <p className="text-[9px] text-slate-400">Role-based access · full audit trail</p>
        </div>
      </div>

      <p className="text-[10px] font-semibold text-slate-500 mb-2">Roles &amp; access</p>
      <div className="mb-4">
        {roles.map((label, i) => (
          <motion.div
            key={label}
            className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 mb-1.5 last:mb-0"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 4, repeat: Infinity, delay: i * 0.5, ease: 'easeInOut' }}
          >
            <span className="text-[10px] text-slate-600">{label}</span>
            <span className="text-[9px] font-medium text-[#004D4D] bg-[#66B3B3]/15 rounded-full px-2 py-0.5">
              Granted
            </span>
          </motion.div>
        ))}
      </div>

      <p className="text-[10px] font-semibold text-slate-500 mb-2">Audit log</p>
      <div className="space-y-1.5">
        {logs.map((line, i) => (
          <motion.p
            key={line}
            className="text-[9px] text-slate-400"
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: [0, 1, 1, 0], x: [-6, 0, 0, 0] }}
            transition={{ duration: 6, repeat: Infinity, delay: i * 1.8, times: [0, 0.1, 0.85, 1] }}
          >
            {line}
          </motion.p>
        ))}
      </div>
    </MiniAppFrame>
  );
};

/* ---------- Custom Software ---------- */
export const CustomIllustration = () => {
  const lines = ['function build(scope) {', '  audit(scope);', '  provision();', '  deploy();', '}'];
  const steps = ['Scoping requirements', 'Building system', 'Running tests', 'Deployed'];
  return (
    <MiniAppFrame title="Build pipeline" activeIcon={Home}>
      <div className="rounded-xl bg-[#003333] p-4 font-mono mb-4">
        <div className="flex gap-1.5 mb-3">
          <span className="w-2 h-2 rounded-full bg-white/20" />
          <span className="w-2 h-2 rounded-full bg-white/20" />
          <span className="w-2 h-2 rounded-full bg-white/20" />
        </div>
        {lines.map((line, i) => (
          <motion.p
            key={i}
            className="text-[10px] text-white/80 leading-relaxed whitespace-pre"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0, 1, 1, 1] }}
            transition={{ duration: 7, repeat: Infinity, times: [0, i * 0.08, i * 0.08 + 0.05, 0.8, 1] }}
          >
            {line}
          </motion.p>
        ))}
        <motion.span
          className="inline-block w-1.5 h-3 bg-[#66B3B3] mt-1"
          animate={{ opacity: [1, 0, 1] }}
          transition={{ duration: 0.8, repeat: Infinity }}
        />
      </div>

      <div className="space-y-2">
        {steps.map((step, i) => (
          <motion.div key={step} className="flex items-center gap-2">
            <motion.span
              className="w-4 h-4 rounded-full border border-[#66B3B3] flex items-center justify-center shrink-0"
              animate={{ backgroundColor: ['#ffffff', '#66B3B3', '#66B3B3'] }}
              transition={{ duration: 7, repeat: Infinity, times: [0, 0.55 + i * 0.1, 1], delay: i * 0.1 }}
            >
              <motion.svg
                width="8" height="8" viewBox="0 0 24 24" fill="none"
                animate={{ opacity: [0, 0, 1] }}
                transition={{ duration: 7, repeat: Infinity, times: [0, 0.55 + i * 0.1, 1], delay: i * 0.1 }}
              >
                <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </motion.svg>
            </motion.span>
            <span className="text-[10px] text-slate-500">{step}</span>
          </motion.div>
        ))}
      </div>
    </MiniAppFrame>
  );
};
